import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { UsuarioLoginRequest } from '../models/usuarioLoginRequest';
import { ForgotPasswordRequest } from '../models/forgotPasswordRequest.model';
import { environment } from '../environments/environment';
import { LoginHelper } from '../helpers/login.helper';
import { UsuarioLoginResponse } from '../models/usuarioLoginResponse.model';
import { ToastService } from './toast.service';
import { HttpErrorResponse } from '@angular/common/http';
import { VerifyEmail } from '../models/verifyEmail.model';
import { getErrorMessage } from '../helpers/error-message.helper';

const options = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  observe: 'response' as 'response'
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  httpClient = inject(HttpClient);
  router = inject(Router);
  loginHelper = inject(LoginHelper);
  toastService = inject(ToastService);

  private readonly signInEndpoint = `${environment.advContabilApi}/api/auth/Login/signin`;
  private readonly signUpEndpoint = `${environment.advContabilApi}/api/auth/Cadastrar/signup`;
  private readonly forgotPassword = `${environment.advContabilApi}/api/auth/EnviarEmailEsqueciSenha/forgot-password`;
  private readonly resetPassword = `${environment.advContabilApi}/api/auth/AlterarSenha/reset-password`;
  private readonly verifyEmailEndpoint = `${environment.advContabilApi}/api/auth/VerificarUsuarioEmail/verify-user-email`;
  private readonly signUpEmailEndpoint = `${environment.advContabilApi}/api/auth/VerificarEmailSend/verify-email-send`;

  private authStatus = new BehaviorSubject<boolean>(this.isAuthenticated());

  authStatus$ = this.authStatus.asObservable();

  usuarioCadastro(formData: FormData): Observable<any> {
    return this.httpClient.post<any>(this.signUpEndpoint, formData, {}).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(getErrorMessage(error, 'Falha no cadastro. Verifique os dados e tente novamente.')));
      })
    );
  }

  usuarioLoginRequest(usuario: UsuarioLoginRequest): Observable<void> {
    return this.httpClient.post<UsuarioLoginResponse>(this.signInEndpoint, usuario).pipe(
      switchMap((res) => {
        if (!res.sucesso) throw new Error(res.erros?.[0] || 'Falha na autenticação');

        return this.loginHelper.signIn(res).pipe(
          tap(() => this.authStatus.next(true))
        );
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(getErrorMessage(error, 'Falha na autenticação. Verifique e-mail e senha.')));
      })
    );
  }

  logout(): void {
    this.loginHelper.signOut();
    this.authStatus.next(false);
  }

  markLoggedOut(): void {
    this.authStatus.next(false);
  }

  esqueceuSenha(email: ForgotPasswordRequest): Observable<any> {
    return this.httpClient.post<HttpResponse<any>>(this.forgotPassword, email, options).pipe(
      tap((resposta) => {
      }),
      catchError((error) => {
        console.error('Erro ao enviar solicitação de recuperação de senha', error);
        return throwError(() => new Error(getErrorMessage(error, 'Falha ao enviar recuperação de senha.')));
      })
    );
  }

  resetarSenha(passwords: any): Observable<any> {
    return this.httpClient.put<any>(`${this.resetPassword}`, passwords).pipe(
      tap((resposta) => {
      }),
      catchError((error) => {
        console.error('Erro ao redefinir senha', error);
        return throwError(() => new Error(getErrorMessage(error, 'Falha ao redefinir senha. O link pode ter expirado.')));
      })
    );
  }

  verifyToken(verifyEmail: VerifyEmail): Observable<{ message?: string }> {
    const body = {
      email: verifyEmail.email || verifyEmail.Email,
      token: (verifyEmail.token || verifyEmail.Token || '').replace(/ /g, '+')
    };

    return this.httpClient.post<{ message?: string }>(this.verifyEmailEndpoint, body).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Erro ao verificar token', error);
        return throwError(() => new Error(getErrorMessage(
          error,
          'Não foi possível verificar o e-mail. O link pode ter expirado; solicite um novo.'
        )));
      })
    );
  }

  signupEmail(email: string): Observable<any> {
    const params = { email };
    return this.httpClient.get<any>(this.signUpEmailEndpoint, { params }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return throwError(() => ({ userExists: false, email, status: 404 }));
        }
        console.error('Erro ao enviar e-mail de inscrição', error);
        return throwError(() => new Error(error.error?.message || 'Falha ao enviar e-mail de inscrição'));
      })
    );
  }

  isAuthenticated(): boolean {
    return this.loginHelper.get() !== null;
  }

  obterUsuarioPorId(id: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.advContabilApi}/api/auth/ObterUsuarioPorId/get-user-by-id/${id}`);
  }

  obterUsuarioAllPropertiesPorId(id: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.advContabilApi}/api/auth/ObterTodosAtributosPorId/get-all-properties-by-id/${id}`);
  }

  retornarDataVerificacaoEmailUsuario(id: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.advContabilApi}/api/auth/RetornarDataVerificacaoEmailUsuario/return-date-by-id/${id}`);
  }

  retornarCnpjUsuario(id: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.advContabilApi}/api/auth/RetornarCnpjUsuario/return-cnpj-by-id/${id}`);
  }

  obterDetalhesEmpresa(usuarioId: string): Observable<any> {
    return this.httpClient.get<any>(`${environment.advContabilApi}/api/auth/ObterDetalhesEmpresa/get-empresa-detalhes/${usuarioId}`);
  }

  atualizarCertificado(usuarioId: string, formData: FormData): Observable<any> {
    return this.httpClient.put<any>(`${environment.advContabilApi}/api/auth/AtualizarCertificado/atualizar-certificado/${usuarioId}`, formData).pipe(
      catchError((error) => {
        console.error('Erro ao atualizar certificado', error);
        return throwError(() => new Error(error.error?.message || 'Falha ao atualizar certificado'));
      })
    );
  }

  atualizarDadosUsuario(usuarioId: string, dados: any): Observable<any> {
    return this.httpClient.put<any>(`${environment.advContabilApi}/api/auth/AtualizarDadosUsuario/atualizar-dados-usuario/${usuarioId}`, dados).pipe(
      catchError((error) => {
        console.error('Erro ao atualizar dados do usuário', error);
        return throwError(() => new Error(error.error?.message || 'Falha ao atualizar dados'));
      })
    );
  }
}
